import type {
  AssignUserCostCenterParams,
  CostCenter,
  CostCenterHistoryEntry,
  CostCenterInconsistenciesResponse,
  CostCenterInconsistencyFinding,
  CostCenterSummary,
  CreateCostCenterParams,
  UpdateCostCenterParams,
} from '@src/models/interfaces/costCenter.interface';
import { CostCenterMapper } from '@src/models/mappers/costCenter.mapper';
import CostCenterRepository from '@src/repositories/CostCenterRepository';

class CostCenterService {
  private repository: CostCenterRepository;

  constructor(repository = new CostCenterRepository()) {
    this.repository = repository;
  }

  async list(organizationId: string, active?: boolean): Promise<CostCenter[]> {
    const { costCenters } = await this.repository.list({
      organizationId,
      ...(active === undefined ? {} : { active }),
    });
    return costCenters.map(CostCenterMapper.fromApi);
  }

  async listSummaries(organizationId: string, activeOnly = true): Promise<CostCenterSummary[]> {
    const list = await this.list(organizationId, activeOnly ? true : undefined);
    return list
      .filter(cc => (activeOnly ? cc.active : true))
      .map(CostCenterMapper.toSummary)
      .sort((a, b) => a.code.localeCompare(b.code) || a.name.localeCompare(b.name));
  }

  async get(organizationId: string, costCenterId: string): Promise<CostCenter | null> {
    try {
      const { costCenter } = await this.repository.get({ organizationId, costCenterId });
      return CostCenterMapper.fromApi(costCenter);
    } catch {
      return null;
    }
  }

  create(params: CreateCostCenterParams): Promise<string> {
    return this.repository.create(params);
  }

  update(params: UpdateCostCenterParams): Promise<string> {
    return this.repository.update(params);
  }

  setActive(organizationId: string, costCenterId: string, active: boolean): Promise<string> {
    return this.repository.setActive({ organizationId, costCenterId, active });
  }

  inconsistencies(organizationId: string): Promise<CostCenterInconsistenciesResponse> {
    return this.repository.inconsistencies(organizationId);
  }

  /**
   * On-demand full-org scan (callable). Used for manual refresh / fallback.
   * Returns open findings only — the callable does not persist status.
   */
  async scanInconsistencies(
    organizationId: string,
  ): Promise<CostCenterInconsistencyFinding[]> {
    const response = await this.repository.inconsistencies(organizationId);
    return CostCenterMapper.fromInconsistenciesScan(organizationId, response);
  }

  subscribeInconsistencies(
    organizationId: string,
    onChange: (findings: CostCenterInconsistencyFinding[]) => void,
    onError: (error: Error) => void,
  ): () => void {
    return this.repository.subscribeInconsistencies(
      organizationId,
      docs => {
        const findings = docs
          .map(({ id, data }) => CostCenterMapper.fromInconsistencyFindingDTO(id, data))
          .filter((finding): finding is CostCenterInconsistencyFinding => finding != null);
        onChange(findings);
      },
      onError,
    );
  }

  assignUser(params: AssignUserCostCenterParams) {
    return this.repository.assignUser(params);
  }

  async listUserHistory(organizationId: string, userId: string): Promise<CostCenterHistoryEntry[]> {
    const { entries } = await this.repository.listUserHistory(organizationId, userId);
    return entries;
  }
}

export default CostCenterService;
